import React from 'react';
import { Badge } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine,
  faCreditCard,
  faUsers,
  faMobile,
  faChartBar,
  faHeadset,
  faClipboardList,
} from '@fortawesome/free-solid-svg-icons';
import AnalysisCard from './AnalysisCard';
import {
  AnalysisRow,
  AnalysisLabel,
  AnalysisProgress,
  AnalysisDescription,
  GenerateReportButton,
} from './Styles';
import {
  SECTION_TITLES,
  ANALYSIS_LABELS,
  DEFAULT_NA,
} from './constants';
import type { AnalysisSectionsProps } from './types';

const formatRevenue = (value: number): string => `$${value.toFixed(2)}`;
const formatPercent = (value: number): string => `${value.toFixed(1)}%`;

const AnalysisSections: React.FC<AnalysisSectionsProps> = ({
  overview,
  activeSessions,
}) => {
  const totalRevenue = overview ? formatRevenue(overview.totalSales) : '$0.00';
  const successRate = overview ? formatPercent(overview.transactionSuccessRate) : '95.0%';
  const avgValue = overview ? formatRevenue(overview.averageOrderValue) : '$25.00';

  return (
    <>
      <AnalysisCard
        title={SECTION_TITLES.REVENUE}
        icon={<FontAwesomeIcon icon={faChartLine} />}
        iconColor="#22c55e"
      >
        <AnalysisRow>
          <AnalysisLabel>{ANALYSIS_LABELS.TOTAL_REVENUE}</AnalysisLabel>
          <Badge count={totalRevenue} color="green" />
        </AnalysisRow>
        <AnalysisProgress percent={70} strokeColor="#22c55e" />
        <AnalysisDescription>{ANALYSIS_LABELS.REVENUE_GROWTH}</AnalysisDescription>
      </AnalysisCard>

      <AnalysisCard
        title={SECTION_TITLES.TRANSACTION}
        icon={<FontAwesomeIcon icon={faCreditCard} />}
        iconColor="#3b82f6"
      >
        <AnalysisRow>
          <AnalysisLabel>{ANALYSIS_LABELS.SUCCESS_RATE}</AnalysisLabel>
          <Badge count={successRate} color="blue" />
        </AnalysisRow>
        <AnalysisRow>
          <AnalysisLabel>{ANALYSIS_LABELS.AVG_VALUE}</AnalysisLabel>
          <Badge count={avgValue} color="purple" />
        </AnalysisRow>
        <AnalysisProgress percent={85} strokeColor="#3b82f6" />
      </AnalysisCard>

      <AnalysisCard
        title={SECTION_TITLES.MERCHANT}
        icon={<FontAwesomeIcon icon={faUsers} />}
        iconColor="#f59e0b"
      >
        <AnalysisRow>
          <AnalysisLabel>{ANALYSIS_LABELS.TOP_MERCHANTS}</AnalysisLabel>
          <Badge count={DEFAULT_NA} color="orange" />
        </AnalysisRow>
        <AnalysisDescription>{ANALYSIS_LABELS.TOP_MERCHANTS_NA}</AnalysisDescription>
      </AnalysisCard>

      <AnalysisCard
        title={SECTION_TITLES.DEVICE}
        icon={<FontAwesomeIcon icon={faMobile} />}
        iconColor="#22c55e"
      >
        <AnalysisRow>
          <AnalysisLabel>{ANALYSIS_LABELS.ACTIVE_DEVICES}</AnalysisLabel>
          <Badge count={activeSessions} color="green" />
        </AnalysisRow>
        <AnalysisRow>
          <AnalysisLabel>{ANALYSIS_LABELS.DEVICE_ERRORS}</AnalysisLabel>
          <Badge count={DEFAULT_NA} color="red" />
        </AnalysisRow>
        <AnalysisProgress percent={90} strokeColor="#22c55e" />
      </AnalysisCard>

      <AnalysisCard
        title={SECTION_TITLES.CUSTOMER}
        icon={<FontAwesomeIcon icon={faChartBar} />}
        iconColor="#14b8a6"
      >
        <AnalysisRow>
          <AnalysisLabel>{ANALYSIS_LABELS.RETENTION_RATE}</AnalysisLabel>
          <Badge count={DEFAULT_NA} color="cyan" />
        </AnalysisRow>
        <AnalysisRow>
          <AnalysisLabel>{ANALYSIS_LABELS.FEEDBACK_SCORE}</AnalysisLabel>
          <Badge count={DEFAULT_NA} color="purple" />
        </AnalysisRow>
        <AnalysisProgress percent={78} strokeColor="#14b8a6" />
      </AnalysisCard>

      <AnalysisCard
        title={SECTION_TITLES.SUPPORT}
        icon={<FontAwesomeIcon icon={faHeadset} />}
        iconColor="#3b82f6"
      >
        <AnalysisRow>
          <AnalysisLabel>{ANALYSIS_LABELS.RESOLUTION_TIME}</AnalysisLabel>
          <Badge count={DEFAULT_NA} color="blue" />
        </AnalysisRow>
        <AnalysisRow>
          <AnalysisLabel>{ANALYSIS_LABELS.COMMON_ISSUES}</AnalysisLabel>
          <Badge count={DEFAULT_NA} color="orange" />
        </AnalysisRow>
        <AnalysisDescription>{ANALYSIS_LABELS.COMMON_ISSUES_NA}</AnalysisDescription>
      </AnalysisCard>

      <AnalysisCard
        title={SECTION_TITLES.CUSTOM_REPORTS}
        icon={<FontAwesomeIcon icon={faClipboardList} />}
        iconColor="#7c3aed"
      >
        <AnalysisDescription>{ANALYSIS_LABELS.CUSTOM_REPORTS_DESC}</AnalysisDescription>
        <GenerateReportButton type="primary" size="large">
          {ANALYSIS_LABELS.GENERATE_REPORT}
        </GenerateReportButton>
      </AnalysisCard>
    </>
  );
};

export default React.memo(AnalysisSections);
